import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AGENT_PROMPT_STATUS_LABELS,
  AGENT_RUN_OUTCOME_LABELS,
  type AgentGuardrailsDTO,
  type AgentGuardrailsInput,
  type AgentManualLineResult,
  type AiModelPriceDTO,
  type AgentOverviewDTO,
  type AgentPromptDTO,
  type AgentPromptRowDTO,
  type AgentPromptStatus,
  type AgentRunDetailDTO,
  type AgentRunOutcome,
  type AgentRunRowDTO,
  type AgentSimulateResult,
} from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Input,
  Kpi,
  PageHeader,
  Select,
  Skeleton,
  Switch,
  Textarea,
  useToast,
  type Tone,
} from '@bifeai/ui';
import { Bot, CircleCheck, FilePlus2, MessagesSquare, Phone, Power, Rocket, Save, Send, ShieldAlert, Store } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { formatDateTime, timeAgo } from '../lib/format';
import { useCan, useMeQuery } from '../lib/session';

type Tab = 'clients' | 'prompt' | 'guardrails' | 'runs';

const PROMPT_TONES: Record<AgentPromptStatus, Tone> = { DRAFT: 'warning', PUBLISHED: 'success', ARCHIVED: 'neutral' };
const RUN_TONES: Record<AgentRunOutcome, Tone> = { REPLIED: 'success', HANDOFF: 'info', MUTED: 'neutral', BLOCKED: 'warning', ERROR: 'danger' };
const AUTHOR: Record<string, string> = { CUSTOMER: 'Cliente', AGENT: 'Agente', STAFF: 'Carnicería', SYSTEM: 'Sistema' };

function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { key: T; label: string }[];
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="bf-segmented" role="tablist" aria-label={label} style={{ width: 'fit-content', maxWidth: '100%', overflowX: 'auto' }}>
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          role="tab"
          aria-selected={value === o.key}
          aria-pressed={value === o.key}
          onClick={() => onChange(o.key)}
          style={{ width: 'auto', padding: '0 14px', fontWeight: 600, fontSize: 'var(--bf-text-sm)', whiteSpace: 'nowrap' }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Agentes IA: el agente de ventas por chat de todos los clientes. El prompt y el cerco son universales:
 * lo que se publica acá rige para todas las carnicerías con el módulo Pedidos y chat.
 */
export function AgentsPage() {
  const [tab, setTab] = useState<Tab>('clients');
  const overview = useQuery({
    queryKey: ['agents', 'overview'],
    queryFn: () => api.get<AgentOverviewDTO>('/platform/agents/overview'),
    refetchInterval: 60_000,
  });
  const issues = overview.data?.issuesOpen ?? 0;

  return (
    <>
      <PageHeader
        title="Agentes IA"
        subtitle="El agente que atiende el WhatsApp de las carnicerías. El prompt y el cerco que publiques acá rigen para todos los clientes."
      />
      {overview.data?.killSwitch && (
        <Callout tone="danger">
          <strong>Todos los agentes están apagados.</strong> Los mensajes entran a la bandeja de cada carnicería para que conteste una persona. Se
          vuelven a prender en la pestaña Cerco.
        </Callout>
      )}
      <Segmented
        label="Secciones de Agentes IA"
        value={tab}
        onChange={setTab}
        options={[
          { key: 'clients', label: 'Clientes' },
          { key: 'prompt', label: 'Prompt' },
          { key: 'guardrails', label: 'Cerco' },
          { key: 'runs', label: issues > 0 ? `Errores del agente (${issues})` : 'Errores del agente' },
        ]}
      />
      {tab === 'clients' && <ClientsTab overview={overview.data} loading={overview.isPending} />}
      {tab === 'prompt' && <PromptTab />}
      {tab === 'guardrails' && <GuardrailsTab />}
      {tab === 'runs' && <RunsTab />}
    </>
  );
}

// ================================================================ clientes
function ClientsTab({ overview, loading }: { overview?: AgentOverviewDTO; loading: boolean }) {
  const me = useMeQuery();
  const models = useAiModels();
  const [manual, setManual] = useState(false);
  if (loading || !overview) {
    return (
      <div className="bf-kpi-grid">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} style={{ height: 116 }} />
        ))}
      </div>
    );
  }
  const lines = overview.clients.reduce((n, c) => n + c.lines.active, 0);
  return (
    <>
      <div className="bf-kpi-grid">
        <Kpi
          label="Clientes con el módulo"
          icon={<Store />}
          value={overview.clients.length}
          delta={lines === 1 ? '1 línea de WhatsApp activa' : `${lines} líneas de WhatsApp activas`}
          deltaMuted
        />
        <Kpi
          label="Prompt publicado"
          icon={<Bot />}
          tone="celeste"
          value={overview.publishedVersion ? `Versión ${overview.publishedVersion}` : 'Ninguno'}
          delta={
            overview.publishedModel
              ? (models.data?.find((m) => m.model === overview.publishedModel)?.label ?? overview.publishedModel)
              : 'Publicá uno en la pestaña Prompt'
          }
          deltaMuted
        />
        <Kpi label="Casos para revisar" icon={<ShieldAlert />} tone="amber" value={overview.issuesOpen} delta="En Errores del agente" deltaMuted />
      </div>
      <Card>
        <CardHeader
          title="Clientes con Pedidos y chat"
          subtitle="El módulo se activa o se quita en la ficha de cada cliente, como los demás módulos."
          actions={
            me.data?.admin.role === 'OWNER' &&
            overview.clients.length > 0 && (
              <Button variant="secondary" size="sm" icon={<Phone />} onClick={() => setManual(true)}>
                Cargar línea a mano
              </Button>
            )
          }
        />
        <CardBody flush>
          {overview.clients.length === 0 ? (
            <EmptyState
              icon={<MessagesSquare />}
              title="Ningún cliente tiene el módulo todavía"
              text="Activalo en Clientes → ficha del cliente → Módulos."
            />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Líneas</th>
                    <th>Clave de IA</th>
                    <th>Respuestas 7 días</th>
                    <th>Pedidos 7 días</th>
                    <th>Para revisar</th>
                  </tr>
                </thead>
                <tbody>
                  {overview.clients.map((c) => (
                    <tr key={c.tenantId}>
                      <td className="bf-cell-main">{c.tenantName}</td>
                      <td>
                        {c.lines.total === 0 ? (
                          <span className="bf-cell-sub">Sin conectar</span>
                        ) : (
                          <>
                            {c.lines.active} de {c.lines.total} activas
                            {c.lines.withError > 0 && (
                              <div>
                                <Badge tone="danger" dot>
                                  {c.lines.withError} con error
                                </Badge>
                              </div>
                            )}
                          </>
                        )}
                      </td>
                      <td>
                        {c.aiKeys.total === 0 ? (
                          <span className="bf-cell-sub">Sin cargar</span>
                        ) : c.aiKeys.failing > 0 ? (
                          <Badge tone="danger" dot>
                            {c.aiKeys.failing} con problema
                          </Badge>
                        ) : c.aiKeys.paused > 0 ? (
                          <Badge tone="warning" dot>
                            Pausado por la carnicería
                          </Badge>
                        ) : (
                          <Badge tone="success" dot>
                            Funcionando
                          </Badge>
                        )}
                      </td>
                      <td className="bf-num">{c.repliesLast7d}</td>
                      <td className="bf-num">{c.ordersLast7d}</td>
                      <td className="bf-num">{c.issuesOpen > 0 ? <Badge tone="warning">{c.issuesOpen}</Badge> : 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      {manual && <ManualLineDialog clients={overview.clients} onClose={() => setManual(false)} />}
    </>
  );
}

/**
 * Carga a mano una línea de WhatsApp (el número de prueba de Meta) en un cliente, con el token permanente de un
 * usuario del sistema del portfolio de BifeAI. Las carnicerías conectan la suya desde la app con el botón de Meta.
 */
function ManualLineDialog({ clients, onClose }: { clients: AgentOverviewDTO['clients']; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState({ tenantId: clients[0]?.tenantId ?? '', phoneNumberId: '', wabaId: '', accessToken: '' });
  const save = useMutation({
    mutationFn: () => api.post<AgentManualLineResult>('/platform/agents/manual-line', f),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['agents', 'overview'] });
      toast.success(
        `Línea ${r.displayPhone ?? ''} conectada`,
        r.branchLinked ? 'Ya atiende a la sucursal del cliente.' : 'El cliente tiene que elegir qué sucursales atiende en Configurar agente.',
      );
      onClose();
    },
    onError: (err) => toast.error('No se pudo conectar', errorMessage(err)),
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Cargar una línea de WhatsApp a mano"
      description="Para el número de prueba de Meta o casos especiales. Las carnicerías conectan su número desde la app, con el botón Conectar WhatsApp."
    >
      <div className="bf-form">
        <Field label="Cliente">
          <Select value={f.tenantId} onChange={(e) => setF({ ...f, tenantId: e.target.value })}>
            {clients.map((c) => (
              <option key={c.tenantId} value={c.tenantId}>
                {c.tenantName}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Identificador del número (Phone Number ID)" hint="Meta → WhatsApp → Paso 1: Pruébalo">
          <Input value={f.phoneNumberId} onChange={(e) => setF({ ...f, phoneNumberId: e.target.value.trim() })} inputMode="numeric" />
        </Field>
        <Field label="Identificador de la cuenta de WhatsApp (WABA)">
          <Input value={f.wabaId} onChange={(e) => setF({ ...f, wabaId: e.target.value.trim() })} inputMode="numeric" />
        </Field>
        <Field
          label="Token permanente"
          hint="Del usuario del sistema del portfolio Bife AI, con los permisos de WhatsApp y sin vencimiento. Se guarda cifrado y no se vuelve a mostrar."
        >
          <Input type="password" autoComplete="off" value={f.accessToken} onChange={(e) => setF({ ...f, accessToken: e.target.value })} />
        </Field>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            icon={<Phone />}
            loading={save.isPending}
            disabled={!f.tenantId || !f.phoneNumberId || !f.wabaId || f.accessToken.length < 20}
            onClick={() => save.mutate()}
          >
            Conectar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ================================================================ prompt
function PromptTab() {
  const me = useMeQuery();
  const isOwner = me.data?.admin.role === 'OWNER';
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery({ queryKey: ['agents', 'prompts'], queryFn: () => api.get<AgentPromptRowDTO[]>('/platform/agents/prompts') });
  const [selected, setSelected] = useState<string | null>(null);
  const current =
    selected ??
    list.data?.find((p) => p.status === 'DRAFT')?.id ??
    list.data?.find((p) => p.status === 'PUBLISHED')?.id ??
    list.data?.[0]?.id ??
    null;

  const create = useMutation({
    mutationFn: (fromId: string | null) => api.post<AgentPromptDTO>('/platform/agents/prompts', { fromId }),
    onSuccess: async (p) => {
      toast.success(`Borrador versión ${p.version} creado`);
      await qc.invalidateQueries({ queryKey: ['agents'] });
      setSelected(p.id);
    },
    onError: (err) => toast.error('No se pudo crear el borrador', errorMessage(err)),
  });

  if (list.isPending) return <Skeleton style={{ height: 320 }} />;
  if (list.data?.length === 0) {
    return (
      <EmptyState icon={<Bot />} title="Todavía no hay prompt" text="El Administrador carga el borrador inicial de BifeAI al abrir esta pestaña." />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', alignItems: 'start' }}>
      <Card>
        <CardHeader title="Versiones" subtitle="Una sola publicada a la vez. Lo publicado no se edita: se crea un borrador nuevo." />
        <CardBody flush>
          <div className="bf-table-wrap">
            <table className="bf-table">
              <tbody>
                {list.data?.map((p) => (
                  <tr key={p.id} data-clickable="true" aria-selected={p.id === current} onClick={() => setSelected(p.id)}>
                    <td>
                      <div className="bf-cell-main">Versión {p.version}</div>
                      <div className="bf-cell-sub">
                        {p.publishedAt
                          ? `Publicada ${timeAgo(p.publishedAt)}${p.publishedByName ? ` por ${p.publishedByName}` : ''}`
                          : `Creada ${timeAgo(p.createdAt)}`}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Badge tone={PROMPT_TONES[p.status]} dot>
                        {AGENT_PROMPT_STATUS_LABELS[p.status]}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
      <div style={{ gridColumn: 'span 2', minWidth: 0, display: 'grid', gap: 16, alignContent: 'start' }}>
        {current && <PromptEditor key={current} id={current} isOwner={isOwner} onCopy={(id) => create.mutate(id)} copying={create.isPending} />}
        {current && <Simulator promptId={current} />}
      </div>
    </div>
  );
}

function PromptEditor({ id, isOwner, onCopy, copying }: { id: string; isOwner: boolean; onCopy: (id: string) => void; copying: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const prompt = useQuery({ queryKey: ['agents', 'prompt', id], queryFn: () => api.get<AgentPromptDTO>(`/platform/agents/prompts/${id}`) });
  const [body, setBody] = useState('');
  const [model, setModel] = useState('');
  const models = useAiModels();
  const [notes, setNotes] = useState('');
  const [confirm, setConfirm] = useState(false);
  const p = prompt.data;
  useEffect(() => {
    if (!p) return;
    setBody(p.body);
    setModel(p.model);
    setNotes(p.notes ?? '');
  }, [p]);

  const editable = isOwner && p?.status === 'DRAFT';
  const dirty = !!p && (body !== p.body || model !== p.model || notes !== (p.notes ?? ''));
  const save = useMutation({
    mutationFn: () => api.patch<AgentPromptDTO>(`/platform/agents/prompts/${id}`, { body, model, notes: notes || null }),
    onSuccess: async () => {
      toast.success('Borrador guardado');
      await qc.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (err) => toast.error('No se pudo guardar', errorMessage(err)),
  });
  const publish = useMutation({
    mutationFn: async () => {
      if (dirty) await api.patch(`/platform/agents/prompts/${id}`, { body, model, notes: notes || null });
      return api.post<AgentPromptDTO>(`/platform/agents/prompts/${id}/publish`);
    },
    onSuccess: async (res) => {
      toast.success(`Versión ${res.version} publicada`, 'Rige para todos los agentes desde la próxima respuesta.');
      setConfirm(false);
      await qc.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (err) => toast.error('No se pudo publicar', errorMessage(err)),
  });

  if (!p) return <Skeleton style={{ height: 420 }} />;
  return (
    <Card>
      <CardHeader
        title={`Versión ${p.version}`}
        subtitle={
          p.status === 'DRAFT'
            ? 'Borrador: todavía no lo usa ningún agente.'
            : p.status === 'PUBLISHED'
              ? 'Es la que usan hoy todos los agentes.'
              : 'Versión anterior. Podés volver a publicarla.'
        }
        actions={
          isOwner && (
            <>
              {p.status !== 'DRAFT' && (
                <Button variant="secondary" size="sm" icon={<FilePlus2 />} loading={copying} onClick={() => onCopy(p.id)}>
                  Nuevo borrador desde esta
                </Button>
              )}
              {p.status !== 'PUBLISHED' && (
                <Button size="sm" icon={<Rocket />} onClick={() => setConfirm(true)}>
                  {p.status === 'DRAFT' ? 'Publicar' : 'Volver a esta versión'}
                </Button>
              )}
            </>
          )
        }
      />
      <CardBody>
        <div className="bf-form">
          <Field label="Modelo de Claude" hint="El más económico alcanza para la mayoría. Si en pruebas no responde bien, se pasa a Sonnet.">
            <Select value={model} onChange={(e) => setModel(e.target.value)} disabled={!editable}>
              {modelOptions(models.data, model).map((m) => (
                <option key={m.model} value={m.model}>
                  {m.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Instrucciones del agente"
            hint="Lo propio de cada carnicería (nombre, horarios, envíos, pagos, tono) lo agrega el sistema aparte. Acá van las reglas que valen para todos."
          >
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              readOnly={!editable}
              rows={26}
              spellCheck
              style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 13, lineHeight: 1.55 }}
            />
          </Field>
          <Field label="Nota de la versión" hint="Qué cambió y por qué (queda en el historial).">
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} readOnly={!editable} maxLength={500} />
          </Field>
          {editable && dirty && (
            <Callout tone="warning">El simulador prueba la versión guardada: guardá el borrador antes de probar los cambios.</Callout>
          )}
          {editable && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="secondary" icon={<Save />} disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>
                Guardar borrador
              </Button>
            </div>
          )}
          {!isOwner && <p className="bf-cell-sub">Solo el Administrador edita y publica el prompt.</p>}
        </div>
      </CardBody>
      <Dialog
        open={confirm}
        onOpenChange={setConfirm}
        title={p.status === 'DRAFT' ? `¿Publicar la versión ${p.version}?` : `¿Volver a la versión ${p.version}?`}
        description="Rige para los agentes de todos los clientes desde la próxima respuesta. La versión publicada ahora pasa a Anterior y se puede recuperar."
      >
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => setConfirm(false)}>
            Cancelar
          </Button>
          <Button icon={<Rocket />} loading={publish.isPending} onClick={() => publish.mutate()}>
            Publicar
          </Button>
        </div>
      </Dialog>
    </Card>
  );
}

// ================================================================ simulador
type SimMessage = { role: 'customer' | 'agent'; text: string; meta?: string };

/** Chat de prueba: el agente responde con el catálogo y la configuración reales de una carnicería, sin guardar nada. */
function Simulator({ promptId }: { promptId: string }) {
  const can = useCan();
  const toast = useToast();
  const overview = useQuery({ queryKey: ['agents', 'overview'], queryFn: () => api.get<AgentOverviewDTO>('/platform/agents/overview') });
  const [tenantId, setTenantId] = useState('');
  const [branchIds, setBranchIds] = useState<string[]>([]);
  const [model, setModel] = useState('');
  const models = useAiModels();
  const [chat, setChat] = useState<SimMessage[]>([]);
  const [state, setState] = useState<unknown>(null);
  const [text, setText] = useState('');
  const [events, setEvents] = useState<string[]>([]);
  const branchList = useQuery({
    queryKey: ['agents', 'branches', tenantId],
    queryFn: () => api.get<{ id: string; name: string; franchise: boolean }[]>(`/platform/agents/tenants/${tenantId}/branches`),
    enabled: Boolean(tenantId),
  });
  useEffect(() => {
    const first = branchList.data?.find((b) => !b.franchise) ?? branchList.data?.[0];
    setBranchIds(first ? [first.id] : []);
  }, [branchList.data]);
  const reset = () => {
    setChat([]);
    setState(null);
    setEvents([]);
  };
  const send = useMutation({
    mutationFn: (messages: SimMessage[]) =>
      api.post<AgentSimulateResult>('/platform/agents/simulate', {
        tenantId,
        branchIds,
        promptId,
        model: model || null,
        state,
        messages: messages.map(({ role, text: t }) => ({ role, text: t })),
      }),
    onSuccess: (r, messages) => {
      const tools = r.toolCalls.map((t) => `${t.name}${t.ok ? '' : ' (falló)'}`).join(', ');
      const meta = `${AGENT_RUN_OUTCOME_LABELS[r.outcome]} · v${r.promptVersion} · ${r.inputTokens + r.outputTokens} tokens${tools ? ` · ${tools}` : ''}${r.error ? ` · ${r.error}` : ''}`;
      setChat([...messages, { role: 'agent', text: r.reply ?? '(no respondió)', meta }]);
      setState(r.state);
      setEvents((e) => [...e, ...r.events]);
    },
    onError: (err, messages) => {
      setChat(messages.slice(0, -1));
      toast.error('El simulador no respondió', errorMessage(err));
    },
  });
  const submit = () => {
    const t = text.trim();
    if (!t || !tenantId || branchIds.length === 0) return;
    const next = [...chat.filter((m) => m.text !== '(no respondió)'), { role: 'customer' as const, text: t }];
    setChat(next);
    setText('');
    send.mutate(next);
  };

  return (
    <Card>
      <CardHeader
        title="Simulador"
        subtitle="Escribí como si fueras un cliente. Usa el catálogo, los precios y la configuración reales de la carnicería elegida, con la clave de BifeAI. No guarda pedidos ni avisa a nadie."
        actions={
          chat.length > 0 && (
            <Button variant="secondary" size="sm" onClick={reset}>
              Empezar de nuevo
            </Button>
          )
        }
      />
      <CardBody>
        <div className="bf-form">
          <div className="bf-form-row">
            <Field label="Carnicería" hint="Las que tienen el módulo Pedidos y chat">
              <Select
                value={tenantId}
                onChange={(e) => {
                  setTenantId(e.target.value);
                  reset();
                }}
              >
                <option value="">Elegí una</option>
                {overview.data?.clients.map((c) => (
                  <option key={c.tenantId} value={c.tenantId}>
                    {c.tenantName}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Modelo" hint="Vacío: el de la versión">
              <Select value={model} onChange={(e) => setModel(e.target.value)}>
                <option value="">El de la versión</option>
                {modelOptions(models.data, '').map((m) => (
                  <option key={m.model} value={m.model}>
                    {m.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          {branchList.data && branchList.data.length > 1 && (
            <div>
              <p className="bf-label" style={{ marginBottom: 6 }}>
                Sucursales que atiende la línea
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                {branchList.data.map((b) => (
                  <label key={b.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Switch
                      checked={branchIds.includes(b.id)}
                      onCheckedChange={(v) => {
                        setBranchIds((ids) => (v ? [...ids, b.id] : ids.filter((x) => x !== b.id)));
                        reset();
                      }}
                      label={b.name}
                    />
                    {b.name}
                    {b.franchise ? ' (franquicia)' : ''}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div
            style={{
              border: '1px solid var(--bf-line)',
              borderRadius: 'var(--bf-radius-md)',
              minHeight: 220,
              maxHeight: 460,
              overflow: 'auto',
              padding: 12,
              display: 'grid',
              gap: 8,
              alignContent: 'start',
              background: 'var(--bf-surface-sunken)',
            }}
          >
            {chat.length === 0 && <p className="bf-cell-sub">Ejemplo: "Hola, ¿tienen vacío? El sábado hago un asado para 8".</p>}
            {chat.map((m, i) => (
              <div
                key={i}
                style={{
                  justifySelf: m.role === 'customer' ? 'end' : 'start',
                  maxWidth: '85%',
                  padding: '8px 11px',
                  borderRadius: 12,
                  background: m.role === 'customer' ? 'var(--bf-success-soft)' : 'var(--bf-surface)',
                  border: '1px solid var(--bf-line)',
                }}
              >
                <div style={{ whiteSpace: 'pre-wrap' }}>{m.text}</div>
                {m.meta && (
                  <div className="bf-cell-sub" style={{ marginTop: 4 }}>
                    {m.meta}
                  </div>
                )}
              </div>
            ))}
            {send.isPending && <p className="bf-cell-sub">El agente está escribiendo…</p>}
          </div>
          {events.length > 0 && (
            <Callout>
              <strong>En la carnicería habría pasado:</strong> {events.join(' · ')}
            </Callout>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
            style={{ display: 'flex', gap: 8 }}
          >
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={tenantId ? 'Escribí como cliente…' : 'Primero elegí una carnicería'}
              disabled={!tenantId || send.isPending || !can('errors.manage')}
              maxLength={2000}
              aria-label="Mensaje del cliente"
            />
            <Button
              type="submit"
              icon={<Send />}
              loading={send.isPending}
              disabled={!tenantId || branchIds.length === 0 || !text.trim() || !can('errors.manage')}
            >
              Enviar
            </Button>
          </form>
        </div>
      </CardBody>
    </Card>
  );
}

// ================================================================ cerco
const lines = (s: string) =>
  s
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

function GuardrailsTab() {
  const me = useMeQuery();
  const isOwner = me.data?.admin.role === 'OWNER';
  const qc = useQueryClient();
  const toast = useToast();
  const g = useQuery({ queryKey: ['agents', 'guardrails'], queryFn: () => api.get<AgentGuardrailsDTO>('/platform/agents/guardrails') });
  const [f, setF] = useState<{
    topics: string;
    words: string;
    perConv: string;
    perChannel: string;
    chars: string;
    strikes: string;
    kill: boolean;
  } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!g.data) return;
    setF({
      topics: g.data.blockedTopics.join('\n'),
      words: g.data.blockedProductWords.join('\n'),
      perConv: String(g.data.maxRepliesPerConversationDay),
      perChannel: String(g.data.maxRepliesPerChannelDay),
      chars: String(g.data.maxReplyChars),
      strikes: String(g.data.abuseStrikes),
      kill: g.data.killSwitch,
    });
  }, [g.data]);

  const save = useMutation({
    mutationFn: (input: AgentGuardrailsInput) => api.put<AgentGuardrailsDTO>('/platform/agents/guardrails', input),
    onSuccess: async (res) => {
      setErrors({});
      toast.success(res.killSwitch ? 'Cerco guardado: los agentes quedaron apagados' : 'Cerco guardado', 'Rige desde la próxima respuesta.');
      await qc.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (err) => {
      const fields = (err as { fields?: Record<string, string> }).fields;
      if (fields) setErrors(fields);
      toast.error('No se pudo guardar', errorMessage(err));
    },
  });

  if (!f || !g.data) return <Skeleton style={{ height: 320 }} />;
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF({ ...f, [k]: v });
  const submit = (kill = f.kill) =>
    save.mutate({
      blockedTopics: lines(f.topics),
      blockedProductWords: lines(f.words),
      maxRepliesPerConversationDay: Number(f.perConv),
      maxRepliesPerChannelDay: Number(f.perChannel),
      maxReplyChars: Number(f.chars),
      abuseStrikes: Number(f.strikes),
      killSwitch: kill,
    });

  return (
    <>
      <Card>
        <CardHeader
          title="Apagado general"
          subtitle="Apaga el agente de todos los clientes al instante. Los mensajes siguen entrando a la bandeja de cada carnicería para que conteste una persona."
          actions={
            <Button
              variant={f.kill ? 'primary' : 'danger'}
              icon={<Power />}
              disabled={!isOwner}
              loading={save.isPending}
              onClick={() => submit(!f.kill)}
            >
              {f.kill ? 'Prender los agentes' : 'Apagar todos los agentes'}
            </Button>
          }
        />
      </Card>
      <Card>
        <CardHeader
          title="Reglas duras"
          subtitle={`Se controlan en el sistema antes de mandar cada respuesta, además de lo que dice el prompt. Último cambio ${formatDateTime(g.data.updatedAt)}${g.data.updatedByName ? ` por ${g.data.updatedByName}` : ''}.`}
        />
        <CardBody>
          <div className="bf-form">
            <div className="bf-form-row">
              <Field label="Temas prohibidos" hint="Uno por renglón. Además, el agente solo habla de la carnicería." error={errors.blockedTopics}>
                <Textarea value={f.topics} onChange={(e) => set('topics', e.target.value)} rows={8} readOnly={!isOwner} />
              </Field>
              <Field
                label="Productos que no se venden por WhatsApp"
                hint="Uno por renglón. WhatsApp prohíbe vender alcohol y tabaco por chat. Si el nombre del producto tiene una de estas palabras, el agente no lo ofrece."
                error={errors.blockedProductWords}
              >
                <Textarea value={f.words} onChange={(e) => set('words', e.target.value)} rows={8} readOnly={!isOwner} />
              </Field>
            </div>
            <div className="bf-form-row">
              <Field label="Respuestas por conversación por día" hint="Entre 5 y 500" error={errors.maxRepliesPerConversationDay}>
                <Input inputMode="numeric" value={f.perConv} onChange={(e) => set('perConv', e.target.value)} readOnly={!isOwner} />
              </Field>
              <Field
                label="Respuestas por línea por día"
                hint="Tope de seguridad del gasto (entre 50 y 50.000)"
                error={errors.maxRepliesPerChannelDay}
              >
                <Input inputMode="numeric" value={f.perChannel} onChange={(e) => set('perChannel', e.target.value)} readOnly={!isOwner} />
              </Field>
            </div>
            <div className="bf-form-row">
              <Field label="Largo máximo de cada respuesta" hint="En caracteres (entre 200 y 3.000)" error={errors.maxReplyChars}>
                <Input inputMode="numeric" value={f.chars} onChange={(e) => set('chars', e.target.value)} readOnly={!isOwner} />
              </Field>
              <Field
                label="Bromas o mal uso: respuestas antes de silenciar"
                hint="1 = responde una sola vez y si sigue, silencia"
                error={errors.abuseStrikes}
              >
                <Input inputMode="numeric" value={f.strikes} onChange={(e) => set('strikes', e.target.value)} readOnly={!isOwner} />
              </Field>
            </div>
            <label style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <Switch checked={f.kill} onCheckedChange={(v) => set('kill', v)} disabled={!isOwner} label="Agentes apagados" />
              Agentes apagados para todos los clientes
            </label>
            {isOwner ? (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button icon={<Save />} loading={save.isPending} onClick={() => submit()}>
                  Guardar cerco
                </Button>
              </div>
            ) : (
              <p className="bf-cell-sub">Solo el Administrador cambia el cerco.</p>
            )}
          </div>
        </CardBody>
      </Card>
    </>
  );
}

// ================================================================ errores del agente
function RunsTab() {
  const [filter, setFilter] = useState<'open' | 'issues' | 'all'>('open');
  const [openId, setOpenId] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['agents', 'runs', filter],
    queryFn: () => api.get<AgentRunRowDTO[]>(`/platform/agents/runs?filter=${filter}`),
    refetchInterval: 30_000,
  });
  return (
    <>
      <Segmented
        label="Filtrar casos"
        value={filter}
        onChange={setFilter}
        options={[
          { key: 'open', label: 'Para revisar' },
          { key: 'issues', label: 'Todos los casos' },
          { key: 'all', label: 'Todas las respuestas' },
        ]}
      />
      <Card>
        <CardBody flush>
          {list.isPending && (
            <div style={{ padding: '8px 22px 16px', display: 'grid', gap: 12 }}>
              <Skeleton />
              <Skeleton />
            </div>
          )}
          {list.data?.length === 0 && (
            <EmptyState
              icon={<CircleCheck />}
              title={filter === 'open' ? 'Nada para revisar' : 'Sin casos en esta lista'}
              text={
                filter === 'open'
                  ? 'Acá aparecen los errores, lo que frenó el cerco y las conversaciones pasadas a una persona o silenciadas.'
                  : undefined
              }
            />
          )}
          {list.data && list.data.length > 0 && (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Resultado</th>
                    <th>Detalle</th>
                    <th>Prompt</th>
                    <th>Cuándo</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.map((r) => (
                    <tr key={r.id} data-clickable="true" onClick={() => setOpenId(r.id)}>
                      <td className="bf-cell-main">{r.tenantName}</td>
                      <td>
                        <Badge tone={RUN_TONES[r.outcome]} dot>
                          {AGENT_RUN_OUTCOME_LABELS[r.outcome]}
                        </Badge>
                        {r.reviewedAt && <div className="bf-cell-sub">Revisado</div>}
                      </td>
                      <td style={{ maxWidth: 360 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.error ?? '—'}</div>
                        {r.errorCode && <div className="bf-cell-sub">{r.errorCode}</div>}
                      </td>
                      <td className="bf-cell-sub">{r.promptVersion ? `v${r.promptVersion}` : '—'}</td>
                      <td className="bf-cell-sub">{timeAgo(r.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      {openId && <RunDetail id={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}

function RunDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const can = useCan();
  const qc = useQueryClient();
  const toast = useToast();
  const detail = useQuery({ queryKey: ['agents', 'run', id], queryFn: () => api.get<AgentRunDetailDTO>(`/platform/agents/runs/${id}`) });
  const review = useMutation({
    mutationFn: (reviewed: boolean) => api.patch<AgentRunRowDTO>(`/platform/agents/runs/${id}`, { reviewed }),
    onSuccess: async (res) => {
      toast.success(res.reviewedAt ? 'Marcado como revisado' : 'Vuelve a la lista para revisar');
      await qc.invalidateQueries({ queryKey: ['agents'] });
      if (res.reviewedAt) onClose();
    },
    onError: (err) => toast.error('No se pudo actualizar', errorMessage(err)),
  });
  const r = detail.data;
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      wide
      title="Caso del agente"
      description={r ? `${r.tenantName}, ${formatDateTime(r.createdAt)}` : undefined}
    >
      {!r ? (
        <Skeleton style={{ height: 160 }} />
      ) : (
        <div className="bf-form">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Badge tone={RUN_TONES[r.outcome]} dot>
              {AGENT_RUN_OUTCOME_LABELS[r.outcome]}
            </Badge>
            {r.promptVersion && <Badge>Prompt v{r.promptVersion}</Badge>}
            {r.model && <Badge>{r.model}</Badge>}
            <Badge>
              {r.inputTokens + r.outputTokens} tokens · {(r.durationMs / 1000).toFixed(1)} s
            </Badge>
          </div>
          {r.error && (
            <Callout tone={r.outcome === 'ERROR' ? 'danger' : 'warning'}>
              {r.errorCode && <strong>{r.errorCode}: </strong>}
              {r.error}
            </Callout>
          )}
          {r.toolCalls.length > 0 && (
            <p className="bf-cell-sub">Herramientas: {r.toolCalls.map((t) => `${t.name}${t.ok ? '' : ' (falló)'}`).join(', ')}</p>
          )}
          <div>
            <p className="bf-label" style={{ marginBottom: 8 }}>
              Conversación hasta ese momento
            </p>
            <div
              style={{
                border: '1px solid var(--bf-line)',
                borderRadius: 'var(--bf-radius-md)',
                maxHeight: 320,
                overflow: 'auto',
                padding: 12,
                display: 'grid',
                gap: 8,
              }}
            >
              {r.messages.length === 0 && <p className="bf-cell-sub">Sin mensajes.</p>}
              {r.messages.map((m, i) => (
                <div
                  key={i}
                  style={{
                    justifySelf: m.author === 'CUSTOMER' ? 'start' : 'end',
                    maxWidth: '85%',
                    padding: '8px 11px',
                    borderRadius: 12,
                    background: m.internal
                      ? 'var(--bf-warning-soft)'
                      : m.author === 'CUSTOMER'
                        ? 'var(--bf-surface-sunken)'
                        : 'var(--bf-celeste-soft)',
                  }}
                >
                  <div className="bf-cell-sub" style={{ marginBottom: 2 }}>
                    {AUTHOR[m.author] ?? m.author}
                    {m.internal ? ' · nota interna' : ''} · {formatDateTime(m.at)}
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{m.body ?? `[${m.kind.toLowerCase()}]`}</div>
                </div>
              ))}
            </div>
          </div>
          {can('errors.manage') && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              {r.reviewedAt ? (
                <Button variant="secondary" loading={review.isPending} onClick={() => review.mutate(false)}>
                  Volver a revisar
                </Button>
              ) : (
                <Button icon={<CircleCheck />} loading={review.isPending} onClick={() => review.mutate(true)}>
                  Marcar como revisado
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}

/** Modelos con precio cargado en Estadísticas de bifi → Precios de la IA (los retirados no se ofrecen). */
function useAiModels() {
  return useQuery({ queryKey: ['bifi', 'ai-prices'], queryFn: () => api.get<AiModelPriceDTO[]>('/platform/bifi/ai-prices') });
}

/** Opciones del selector, de la más económica a la más cara; el modelo actual aparece aunque esté retirado. */
function modelOptions(list: AiModelPriceDTO[] | undefined, current: string): { model: string; label: string }[] {
  const sorted = [...(list ?? [])].sort((a, b) => a.inputUsd - b.inputUsd || a.outputUsd - b.outputUsd);
  const opts = sorted
    .filter((m) => !m.retiredAt || m.model === current)
    .map((m, i) => ({
      model: m.model,
      label: `${m.label}${m.retiredAt ? ' (retirado por Anthropic)' : i === 0 ? ' (el más económico)' : ''} · USD ${m.inputUsd} / ${m.outputUsd} por millón`,
    }));
  if (current && !opts.some((o) => o.model === current)) opts.unshift({ model: current, label: current });
  return opts;
}
